import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
    ArrowLeftIcon,
    ArrowPathIcon,
    ExclamationTriangleIcon,
    MapPinIcon,
    TruckIcon,
    UserCircleIcon,
    ClockIcon,
    BoltIcon,
    CurrencyDollarIcon,
    SignalIcon,
} from "@heroicons/react/24/outline";

import { Button } from "@/components/ui/Button";
import { trackTrip } from "../api/liveMonitoringApi";

// ============================================================================
// Badges
// ============================================================================

const TripStatusBadge = ({ status }: { status: string }) => {
    const s = status ?? "UNKNOWN";
    const styles: Record<string, string> = {
        PENDING: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400",
        STARTED: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
        DRIVER_ARRIVED: "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400",
        PICKED_UP: "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400",
        IN_PROGRESS: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
        COMPLETED: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
        CANCELLED: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
    };
    return (
        <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${styles[s] || "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${s === "IN_PROGRESS" || s === "PICKED_UP" ? "bg-emerald-500 animate-pulse" : s === "COMPLETED" ? "bg-green-500" : s === "CANCELLED" ? "bg-rose-500" : "bg-blue-500"}`} />
            {s.replace(/_/g, " ")}
        </span>
    );
};

// ============================================================================
// Helpers
// ============================================================================

const fmt = (d: string | null | undefined) => {
    if (!d) return "—";
    try {
        return new Intl.DateTimeFormat("en-NG", { dateStyle: "medium", timeStyle: "short" }).format(new Date(d));
    } catch { return d; }
};

const fmtCurrency = (amount: number | null | undefined, currency = "NGN") => {
    if (amount == null) return "—";
    return new Intl.NumberFormat("en-NG", { style: "currency", currency, maximumFractionDigits: 0 }).format(amount);
};

const InfoRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <div className="flex items-start justify-between gap-4 py-2">
        <span className="text-sm text-slate-500 dark:text-slate-400">{label}</span>
        <span className="text-right text-sm font-medium text-slate-900 dark:text-white">{value || "—"}</span>
    </div>
);

// ============================================================================
// Component
// ============================================================================

export const LiveTripDetailsRoute = () => {
    const { tripId } = useParams<{ tripId: string }>();
    const navigate = useNavigate();

    const { data, isLoading, error, refetch, isFetching } = useQuery({
        queryKey: ["live-trip", tripId],
        queryFn: () => trackTrip(tripId!),
        enabled: !!tripId,
        refetchInterval: 10_000,
    });

    const trip = data?.data;

    // ========================================================================
    // Loading / Error
    // ========================================================================

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
            </div>
        );
    }

    if (error || !trip) {
        return (
            <div className="space-y-4">
                <Button variant="ghost" size="sm" onClick={() => navigate("/live-monitoring/trips")}>
                    <ArrowLeftIcon className="mr-1.5 h-4 w-4" /> Back to Trips
                </Button>
                <div className="rounded-2xl border border-rose-200 bg-rose-50 p-8 text-center dark:border-rose-800 dark:bg-rose-900/20">
                    <ExclamationTriangleIcon className="mx-auto h-10 w-10 text-rose-400" />
                    <h3 className="mt-3 text-lg font-semibold text-rose-700 dark:text-rose-300">Failed to load trip</h3>
                    <p className="mt-1 text-sm text-rose-600 dark:text-rose-400">{(error as Error)?.message ?? "Trip not found"}</p>
                </div>
            </div>
        );
    }

    // ========================================================================
    // Render
    // ========================================================================

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <Button variant="ghost" size="sm" onClick={() => navigate("/live-monitoring/trips")}>
                        <ArrowLeftIcon className="mr-1.5 h-4 w-4" /> Back
                    </Button>
                    <div>
                        <h2 className="text-xl font-bold text-slate-900 dark:text-white">Trip Tracking</h2>
                        <p className="text-sm text-slate-500 dark:text-slate-400">ID: {trip.tripId}</p>
                    </div>
                    <TripStatusBadge status={trip.status} />
                </div>
                <Button variant="secondary" size="sm" onClick={() => refetch()} disabled={isFetching}>
                    <ArrowPathIcon className={`mr-1.5 h-4 w-4 ${isFetching ? "animate-spin" : ""}`} /> Refresh
                </Button>
            </div>

            {/* Progress Card */}
            {trip.progress && (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 dark:border-amber-800 dark:bg-amber-900/20">
                    <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-amber-800 dark:text-amber-300">
                        <BoltIcon className="h-4 w-4" /> Trip Progress
                    </h3>
                    <div className="mb-3 h-2.5 overflow-hidden rounded-full bg-amber-200 dark:bg-amber-800">
                        <div
                            className="h-full rounded-full bg-amber-500 transition-all"
                            style={{ width: `${trip.progress.percentage ?? 0}%` }}
                        />
                    </div>
                    <div className="grid gap-3 sm:grid-cols-4">
                        <div>
                            <p className="text-xs text-amber-600 dark:text-amber-400">Stage</p>
                            <p className="text-sm font-medium text-amber-900 dark:text-amber-200">{trip.progress.currentStage?.replace(/_/g, " ") ?? "—"}</p>
                        </div>
                        <div>
                            <p className="text-xs text-amber-600 dark:text-amber-400">Progress</p>
                            <p className="text-sm font-medium text-amber-900 dark:text-amber-200">{trip.progress.percentage ?? 0}%</p>
                        </div>
                        <div>
                            <p className="text-xs text-amber-600 dark:text-amber-400">Distance Left</p>
                            <p className="text-sm font-medium text-amber-900 dark:text-amber-200">{trip.progress.distanceToDestinationKm ? `${trip.progress.distanceToDestinationKm.toFixed(1)} km` : "—"}</p>
                        </div>
                        <div>
                            <p className="text-xs text-amber-600 dark:text-amber-400">ETA</p>
                            <p className="text-sm font-medium text-amber-900 dark:text-amber-200">{trip.progress.estimatedMinutesToDestination ? `${trip.progress.estimatedMinutesToDestination} min` : "—"}</p>
                        </div>
                    </div>
                    {trip.progress.nextStep && (
                        <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">Next: {trip.progress.nextStep}</p>
                    )}
                </div>
            )}

            {/* Grid */}
            <div className="grid gap-6 lg:grid-cols-2">
                {/* Timestamps */}
                <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                    <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                        <ClockIcon className="h-4 w-4" /> Timeline
                    </h3>
                    <div className="divide-y divide-slate-100 dark:divide-slate-800">
                        <InfoRow label="Started" value={fmt(trip.startedAt)} />
                        <InfoRow label="Driver Arrived" value={fmt(trip.driverArrivedAt)} />
                        <InfoRow label="Passenger Picked Up" value={fmt(trip.passengerPickedUpAt)} />
                        <InfoRow label="Completed" value={fmt(trip.completedAt)} />
                        <InfoRow label="Cancelled" value={fmt(trip.cancelledAt)} />
                    </div>
                </div>

                {/* Locations */}
                <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                    <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                        <MapPinIcon className="h-4 w-4" /> Locations
                    </h3>
                    <div className="space-y-4">
                        <div>
                            <p className="mb-1 text-xs font-medium text-emerald-600 dark:text-emerald-400">Pickup</p>
                            <p className="text-sm text-slate-900 dark:text-white">{trip.pickup?.address || "—"}</p>
                            {trip.pickup && <p className="text-xs text-slate-400">{trip.pickup.latitude?.toFixed(5)}, {trip.pickup.longitude?.toFixed(5)}</p>}
                        </div>
                        <div>
                            <p className="mb-1 text-xs font-medium text-rose-600 dark:text-rose-400">Dropoff</p>
                            <p className="text-sm text-slate-900 dark:text-white">{trip.dropoff?.address || "—"}</p>
                            {trip.dropoff && <p className="text-xs text-slate-400">{trip.dropoff.latitude?.toFixed(5)}, {trip.dropoff.longitude?.toFixed(5)}</p>}
                        </div>
                        {trip.currentLocation && (
                            <div>
                                <p className="mb-1 text-xs font-medium text-blue-600 dark:text-blue-400">Current Location</p>
                                <p className="text-xs text-slate-400">{trip.currentLocation.latitude?.toFixed(5)}, {trip.currentLocation.longitude?.toFixed(5)}</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Driver Info */}
                <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                    <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                        <TruckIcon className="h-4 w-4" /> Driver
                    </h3>
                    <div className="divide-y divide-slate-100 dark:divide-slate-800">
                        <InfoRow label="Name" value={trip.driver ? `${trip.driver.firstName} ${trip.driver.lastName}` : "—"} />
                        <InfoRow label="Phone" value={trip.driver?.phoneNumber} />
                    </div>

                    {trip.driverLiveLocation && (
                        <div className="mt-3 rounded-xl bg-slate-50 p-3 dark:bg-slate-800/50">
                            <p className="mb-2 flex items-center gap-1.5 text-xs font-medium text-slate-600 dark:text-slate-300">
                                <SignalIcon className="h-3.5 w-3.5 text-emerald-500" /> Live Location
                            </p>
                            <div className="grid grid-cols-2 gap-2 text-xs">
                                <div>
                                    <p className="text-slate-400">Lat/Lng</p>
                                    <p className="font-mono text-slate-700 dark:text-slate-200">
                                        {trip.driverLiveLocation.latitude?.toFixed(5)}, {trip.driverLiveLocation.longitude?.toFixed(5)}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-slate-400">Speed</p>
                                    <p className="font-mono text-slate-700 dark:text-slate-200">{trip.driverLiveLocation.speed ?? "—"} km/h</p>
                                </div>
                                <div>
                                    <p className="text-slate-400">Heading</p>
                                    <p className="font-mono text-slate-700 dark:text-slate-200">{trip.driverLiveLocation.heading ?? "—"}°</p>
                                </div>
                                <div>
                                    <p className="text-slate-400">Updated</p>
                                    <p className="font-mono text-slate-700 dark:text-slate-200">{fmt(trip.driverLiveLocation.lastUpdatedAt)}</p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Passenger */}
                <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                    <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                        <UserCircleIcon className="h-4 w-4" /> Passenger
                    </h3>
                    <div className="divide-y divide-slate-100 dark:divide-slate-800">
                        <InfoRow label="Name" value={trip.passenger ? `${trip.passenger.firstName} ${trip.passenger.lastName}` : "—"} />
                        <InfoRow label="Phone" value={trip.passenger?.phoneNumber} />
                    </div>
                </div>

                {/* Vehicle */}
                {trip.vehicle && (
                    <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                        <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                            <TruckIcon className="h-4 w-4" /> Vehicle
                        </h3>
                        <div className="divide-y divide-slate-100 dark:divide-slate-800">
                            <InfoRow label="Plate Number" value={trip.vehicle.plateNumber} />
                            <InfoRow label="Make / Model" value={`${trip.vehicle.make || ""} ${trip.vehicle.model || ""}`} />
                            <InfoRow label="Color" value={trip.vehicle.color} />
                        </div>
                    </div>
                )}

                {/* Fare */}
                <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                    <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                        <CurrencyDollarIcon className="h-4 w-4" /> Fare Details
                    </h3>
                    <div className="divide-y divide-slate-100 dark:divide-slate-800">
                        <InfoRow label="Estimated Fare" value={fmtCurrency(trip.estimatedFare, trip.currency)} />
                        <InfoRow label="Final Fare" value={fmtCurrency(trip.finalFare, trip.currency)} />
                        <InfoRow label="Currency" value={trip.currency} />
                    </div>
                </div>
            </div>

            {/* Booking & Ride Offer */}
            <div className="grid gap-6 lg:grid-cols-2">
                {trip.booking && (
                    <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                        <h3 className="mb-3 text-sm font-semibold text-slate-700 dark:text-slate-200">Related Booking</h3>
                        <div className="divide-y divide-slate-100 dark:divide-slate-800">
                            <InfoRow label="Booking ID" value={trip.booking.bookingId} />
                            <InfoRow label="Status" value={trip.booking.status} />
                            <InfoRow label="Booked At" value={fmt(trip.booking.bookedAt)} />
                        </div>
                    </div>
                )}
                {trip.rideOffer && (
                    <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                        <h3 className="mb-3 text-sm font-semibold text-slate-700 dark:text-slate-200">Related Ride Offer</h3>
                        <div className="divide-y divide-slate-100 dark:divide-slate-800">
                            <InfoRow label="Ride Offer ID" value={trip.rideOffer.rideOfferId} />
                            <InfoRow label="Status" value={trip.rideOffer.status} />
                            <InfoRow label="Available Seats" value={trip.rideOffer.availableSeats} />
                        </div>
                    </div>
                )}
            </div>

            {/* Footer */}
            <div className="text-right text-xs text-slate-400 dark:text-slate-500">
                Auto-refreshing every 10s
            </div>
        </div>
    );
};
